//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState
} from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Divider, Drawer, IconButton, InputLabel, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, MenuItem, Tooltip, Typography,
  useMediaQuery
} from '@mui/material';
import { styled, Theme, useTheme } from '@mui/material/styles';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DnsIcon from '@mui/icons-material/Dns';
import InputIcon from '@mui/icons-material/Input';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DataExplorerIcon from '@mui/icons-material/Explore';
import DatasetIcon from '@mui/icons-material/Storage';
import GraphsIcon from '@mui/icons-material/AccountTree';
import JobIcon from '@mui/icons-material/Work';
import LogoutIcon from '@mui/icons-material/Logout';
import AskAiPill from '@/components/Chat/AskAiPill';
import HeaderControl from '@/components/Header/HeaderControl';
import { HEADER_TOUR_ANCHORS } from '@/components/Header/headerTourAnchors';
import HeaderIdentityMenu from '@/components/Header/HeaderIdentityMenu';
import HeaderTaskbar from '@/components/Header/HeaderTaskbar';
import DemoDataMenu from '@/components/Header/DemoDataMenu';
import DemoDrawerSection from '@/components/Header/DemoDrawerSection';
import SettingsDrawerSection from '@/components/Header/SettingsDrawerSection';
import ToolsPopover from '@/components/Header/ToolsPopover';
import { pluginToolEntry } from '@/components/Header/pluginToolEntry';
import { type ToolEntry } from '@/components/Header/toolEntries';
import {
  DEMO_DATA_TOOL_ID, SERVER_TOOL_ID, usePinnedTools
} from '@/components/Header/usePinnedTools';
import {
  CHAT_PANEL_ID, CHAT_PANEL_TITLE, chatPanelIcon
} from '@/components/Chat/chatPanelId';
import { useChatAvailability } from '@/components/Chat/useChatAvailability';
import { panelIdForRow } from '@/components/Panels/pluginPanelSpec';
import PluginSurfaceDispatcher from '@/components/PluginSurfaceDispatcher';
import {
  ContrastFormControl, ContrastSelect
} from '@/components/Elements/Contrast';
import HelpIcon from '@/components/Help/HelpIcon';
import LLMSelector from '@/components/LLM/LLMSelector';
import SkipLink from '@/components/Elements/SkipLink';
import ExpirationBanner from '@/components/Expiration/ExpirationBanner';
import { PANEL_REGION_ID } from '@/components/Panels/PanelHost';
import { useAuth } from '@/hooks/useAuth';
import { useConfig } from '@/hooks/useConfig';
import { useDataset } from '@/hooks/useDataset';
import { usePanels } from '@/hooks/usePanels';
import { useTargetSelector } from '@/hooks/useTargetSelector';
import { useUI } from '@/hooks/useUI';
import {
  type ActivePlugin, type PluginSurface
} from '@/services/apiTypes';
import {
  pluginSurfaceRowsQuery, type PluginSurfaceRow
} from '@/components/Header/pluginSurfaces';
import { APP_HEADER_ID } from '@/components/Header/headerIds';
import PluginsDrawerSection from '@/components/Header/PluginsDrawerSection';
import { useSettings } from '@/hooks/useSettings';
import { MenuLogo } from '@/theme/Logo';
import { useDemoDataLoader } from '@/utils/DemoDataLoader';
import { ERoutesRoot } from '@/routes/RoutesEnum';

// State for the currently open plugin modal.  The renderer dispatches on
// `surface.kind` to pick the modal component.
interface OpenSurfaceState {
  plugin: ActivePlugin;
  surface: PluginSurface;
}

const headerStyle = (theme: Theme, isSelected: boolean) => ({
  ...(isSelected ? {
    color: theme.palette.menuPanel.selected.text,
    backgroundColor: theme.palette.menuPanel.selected.background,
    '& svg': {
      color: theme.palette.menuPanel.selected.text
    }
  } : {
    color: theme.palette.menuPanel.default.text,
    backgroundColor: theme.palette.menuPanel.default.background,
    '& svg': {
      color: theme.palette.menuPanel.default.text
    }
  }),
  '&:hover': {
    color: theme.palette.menuPanel.hover.text,
    backgroundColor: theme.palette.menuPanel.hover.background,
    '& svg': {
      color: theme.palette.menuPanel.hover.text
    }
  },
  '&.Mui-disabled': {
    color: theme.palette.menuPanel.disabled.text,
    backgroundColor: theme.palette.menuPanel.disabled.background,
    '& svg': {
      color: theme.palette.menuPanel.disabled.text
    }
  }
});

const HeaderMenu = styled('ul')(({theme}) => ({
  listStyleType: 'none', // Ensure no bullets.
  margin: '6px 0px',
  padding: 0,
  display: 'flex',
  gap: '8px',
  flexGrow: 1,
  minWidth: 0, // Allow flex item to shrink below content size.
  overflowX: 'auto',
  scrollbarWidth: 'none', // Firefox.
  '&::-webkit-scrollbar': { // Chrome/Safari.
    display: 'none'
  },
  '& li': {
    cursor: 'pointer',
    padding: '5px 10px',
    borderRadius: theme.border.radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    flexShrink: 0,
    '& svg': { // Style for any child <svg>.
      marginRight: '6px',
    },
    ...(theme?.menuItem && { ...theme.menuItem }),
  },
}));

const HeaderMenuItem = styled('li', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  ...headerStyle(theme, isSelected)
}));

interface AuthMenuItem {
  link: string;
  title: string;
  icon: React.ReactNode;
}

interface HeaderProps {
  selectedMenuItem?: string;
  menuClickCallback?: () => void;
  hidden?: boolean;
}

const Header = ({
  selectedMenuItem = '',
  menuClickCallback,
  hidden
}: HeaderProps) => {
  // The choosable ones rather than every one there is: this picks what the explorer
  // queries and the schema view draws, which a namespace of tables cannot answer for (see
  // choosableDatasets).
  const { activeDataset, choosableDatasets, setActiveDataset } = useDataset();
  // A page may lend the selector entries of its own — the Graphs page adds the
  // graph it is drafting, which has no dataset on the server yet, and which is
  // choosable by definition.
  const { registration } = useTargetSelector();
  const extraOptions = registration?.options ?? [];
  const datasetOptions = useMemo(
    () => choosableDatasets.map((dataset) => (
      { value: dataset.name, label: dataset.name, isAction: false })),
    [choosableDatasets]);
  const selectorOptions = [...datasetOptions, ...extraOptions];
  const selectorValue = registration?.value ??
    (choosableDatasets.length === 0 ? 'Empty' : activeDataset || '');
  const handleSelectorChange = (value: string) => {
    if (registration)
      registration.onSelect(value);
    else
      setActiveDataset(value);
  };
  const { activeUser, isAuthenticated, logout, userProfile } = useAuth();
  const { appConfig, configData } = useConfig();
  const { displayMode } = useUI();
  // Panels are portaled to the body, outside the main landmark, so a keyboard
  // user needs a route to them the same way they need one to the menu.
  const { panels, openPanel } = usePanels();
  const { isDisabled: isChatDisabled, disabledReason: chatDisabledReason } =
    useChatAvailability();
  const hasOpenPanel = panels.some(entry => entry.state === 'open');
  const { openSettings } = useSettings();
  const { loadDemoData, demoLoaded, demoIsLoading } = useDemoDataLoader();
  // Anchored to the identity control, which is where the row that opens it
  // lives: which tools a user keeps is a setting of theirs.
  const [toolsAnchor, setToolsAnchor] = useState<HTMLElement | null>(null);
  // The launcher the demo menu was opened from — a taskbar pill, the Tools
  // row's re-anchor, or the drawer's stand-in. Both the Popper's position and
  // the close-time focus return aim at it, so there is no fixed button ref
  // that could go stale when the launcher varies.
  const [demoAnchorEl, setDemoAnchorEl] = useState<HTMLElement | null>(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [mainElement, setMainElement] = useState<Element | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSurface, setOpenSurface] = useState<OpenSurfaceState | null>(null);

  // Flat row list across every plugin's declared surfaces, fetched once
  // authed.  Empty when no plugins are loaded, none declare a surface, or the
  // manifest was rejected — the Plugins section disappears entirely in those
  // cases.  Validation lives in the query's select, never in this render:
  // the header sits inside the route tree, where a validator's throw would
  // replace every authenticated route with the error page instead of costing
  // the panels, which is what PanelErrorBoundary exists to contain.
  const {
    data: pluginSurfaceRows = [], isSuccess: pluginSurfaceRowsReady
  } = useQuery({
    ...pluginSurfaceRowsQuery(), enabled: !!activeUser,
  });

  // Initialize scroll state from storage to prevent flicker on remount.
  const [isScrolled, setIsScrolled] = useState(() =>
    sessionStorage.getItem('headerIsScrolled') === 'true'
  );
  const [canScroll, setCanScroll] = useState(false);

  const menuRef = useRef<HTMLUListElement>(null);
  const isScrollingRef = useRef(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmall = useMediaQuery(theme.breakpoints.down('smMd'));

  useEffect(() => {
    // Set the reference to the main element once it's available in the DOM.
    setMainElement(document.querySelector('main'));
  }, []);


  const checkScroll = useCallback(() => {
    if (menuRef.current && !isScrollingRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = menuRef.current;

      // Save scroll position.
      sessionStorage.setItem('headerMenuScrollLeft', scrollLeft.toString());

      // Use > 1 instead of > 0 to account for sub-pixel rounding in browsers.
      const scrolled = scrollLeft > 1;
      const scrollable = scrollWidth > clientWidth;

      setIsScrolled(scrolled);
      setCanScroll(scrollable);

      // Persist state.
      sessionStorage.setItem('headerIsScrolled', scrolled.toString());
    }
  }, []);

  const handleScroll = useCallback((direction: 'left' | 'right') => {
    if (menuRef.current) {
      isScrollingRef.current = true;
      const { scrollWidth } = menuRef.current;
      menuRef.current.scrollTo({
        left: direction === 'left' ? 0 : scrollWidth,
        behavior: 'smooth'
      });
      // Update state and check scroll after animation completes.
      setTimeout(() => {
        if (direction === 'left') {
          setIsScrolled(false);
        } else {
          setIsScrolled(true);
        }
        isScrollingRef.current = false;
        checkScroll();
      }, 500);
    }
  }, [checkScroll]);

  useLayoutEffect(() => {
    // Restore scroll position before checking arrows.
    if (menuRef.current) {
      const savedScroll = sessionStorage.getItem('headerMenuScrollLeft');
      if (savedScroll) {
        menuRef.current.scrollLeft = parseInt(savedScroll, 10);
      }
    }
  }, []);

  useLayoutEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [displayMode, checkScroll]); // Re-check if display mode changes
                                  // (icons vs titles).


  const authenticatedMenuItems: AuthMenuItem[] = [
    { link: ERoutesRoot.Dashboard, title: 'Dashboard',
      icon: <DashboardIcon /> },
    { link: ERoutesRoot.Explorer, title: 'Data Explorer',
      icon: <DataExplorerIcon /> },
    { link: ERoutesRoot.Datasets, title: 'Datasets', icon: <DatasetIcon /> },
    { link: ERoutesRoot.Graphs, title: 'Graphs', icon: <GraphsIcon /> },
    { link: ERoutesRoot.Jobs, title: 'Jobs', icon: <JobIcon /> },
  ];

  const handleSettings = () => {
    // The drawer is the narrow-viewport stand-in for the identity menu, so it
    // opens the same personal scope: nothing reached from a personal menu may
    // change what other users of the deployment see.
    openSettings(undefined, 'user');
    // Close the container that launched it: leaving a menu open behind the
    // modal strands its items in the accessibility tree under the dialog.
    setDrawerOpen(false);
    if (menuClickCallback)
      menuClickCallback();
  };

  // The server and admin views ride their own header controls on desktop,
  // and every one of those controls is gone below `sm`: the drawer is where
  // they live on a phone. Same scopes as the desktop controls, so what a
  // user learns on one viewport holds on the other.
  const handleDrawerServerSettings = () => {
    openSettings('serverStatus', 'server');
    setDrawerOpen(false);
  };

  const handleDrawerAdminSettings = () => {
    openSettings('plugins', 'admin');
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      const redirectUrl = appConfig?.logout_redirect_url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
      // Redirect to login page after successful logout.
      navigate(`${ERoutesRoot.Login}?logout=1`);
      setDrawerOpen(false); // Close drawer on mobile after logout.
      if (menuClickCallback)
        menuClickCallback();
    } catch (error) {
      console.error('Failed to logout', error);
    }
  };

  const handleAuthenticatedMenuClick = (item: AuthMenuItem) => {
    navigate(item.link);
    setDrawerOpen(false); // Close drawer on mobile after navigation.
    if (menuClickCallback)
      menuClickCallback();
  };

  // Close whichever container surfaced the click, so the surface is the only
  // thing on screen (mirrors how Settings / Logout collapse the menu).
  const closeMenuContainers = () => {
    setDrawerOpen(false);
  };

  // From the desktop menu, a panel-enabled surface opens its panel: on a
  // viewport that offers panels, such a surface has no modal presentation at
  // all, so the menu row and the pill lead to the same place.
  const handlePluginSurfaceClick = useCallback((row: PluginSurfaceRow) => {
    const panelId = panelIdForRow(row);
    if (panelId) openPanel(panelId);
    else setOpenSurface({ plugin: row.plugin, surface: row.surface });
    setDrawerOpen(false);
  }, [openPanel]);

  // From the drawer, every surface opens as a modal. The drawer is the mobile
  // container, and mobile has no rail to park a panel back into.
  const handleDrawerSurfaceClick = (row: PluginSurfaceRow) => {
    setOpenSurface({ plugin: row.plugin, surface: row.surface });
    closeMenuContainers();
  };

  // Everything a user can open, in one list: the taskbar and the Tools
  // popover both draw from it, so a panel, a modal and a menu-opening action
  // are pinned and ordered by the same code rather than by three special
  // cases. Memoized because the taskbar keys its ranking -- and the pill
  // re-measurement that follows it -- on this list's identity, so a fresh
  // array on every header render would re-measure the rail whenever any
  // header state moved.
  const tools: ToolEntry[] = useMemo(() => [
    {
      id: CHAT_PANEL_ID,
      label: CHAT_PANEL_TITLE,
      name: CHAT_PANEL_TITLE,
      icon: chatPanelIcon({ fontSize: 'inherit' }),
      panelId: CHAT_PANEL_ID,
      // The one tool that can be unusable: chat needs a configured model.
      isDisabled: isChatDisabled,
      disabledReason: chatDisabledReason,
      open: () => openPanel(CHAT_PANEL_ID)
    },
    {
      id: DEMO_DATA_TOOL_ID,
      label: 'Demo data',
      name: 'Load demo data',
      icon: <InputIcon fontSize="inherit" />,
      popup: 'menu',
      isExpanded: demoOpen,
      // Anchors its own dataset menu to whatever was activated, so it works
      // the same from the taskbar and from the Tools list.
      open: (element: HTMLElement) => {
        setDemoAnchorEl(element);
        setDemoOpen(true);
      }
    },
    {
      // Facts about the graph server, readable by everyone and changed by no
      // one -- which is why it is here and not in the personal menu or behind
      // the admin control.
      id: SERVER_TOOL_ID,
      label: 'Server',
      name: 'Server status and configuration',
      icon: <DnsIcon fontSize="inherit" />,
      open: () => openSettings('serverStatus', 'server')
    },
    // Grouped by plugin and alphabetical between them, so a plugin's
    // surfaces sit together and the list a user learns stays put: it changes
    // only when a plugin is added or removed, never with what is pinned or
    // running. `toSorted` is stable, so surfaces keep their manifest order
    // within a plugin.
    ...[...pluginSurfaceRows]
      .toSorted((a, b) => a.plugin.name.localeCompare(b.plugin.name))
      .map((row) => pluginToolEntry(row, handlePluginSurfaceClick))
  ], [
    isChatDisabled, chatDisabledReason, openPanel, demoOpen, openSettings,
    pluginSurfaceRows, handlePluginSurfaceClick
  ]);

  const toolIds = useMemo(
    () => tools.map((tool) => tool.id), [tools]);

  // Readiness is the plugin-surfaces query's own success, not merely
  // `activeUser`: while it is pending or has errored, `tools` only reflects
  // the built-ins, and pruning against that partial list would delete pins
  // for plugins that have not loaded yet rather than ones that are gone.
  const pinnedTools = usePinnedTools(toolIds, pluginSurfaceRowsReady);

  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter') {
      action();
    }
  };

  return (
    <Box
      component="header"
      id={APP_HEADER_ID}
      sx={{
        backgroundColor: theme.palette.menuPanel.default.background,
        padding: '0px 10px',
        display: hidden ? 'none' : 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <SkipLink href="#main-content" text="Skip to main content" />
      <nav style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '2px 0px',
            height: '62px',
            overflow: 'hidden',
            gap: '12px'
          }}
        >
          <MenuLogo sx={{ marginRight: '4px', marginTop: '4px', flexShrink: 0 }}/>
          {isAuthenticated() && (
            <SkipLink href="#application-menu" text="Skip to application menu" />
          )}
          {hasOpenPanel && (
            <SkipLink href={`#${PANEL_REGION_ID}`} text="Skip to open panels" />
          )}
          {isAuthenticated() ? (
            <>
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: 0,
                    flex: '0 1 auto'
                  }}
                >
              {!isMobile && <LLMSelector sx={{ marginTop: '4px', minWidth: '80px', flexShrink: 1 }}/>}
              {!isMobile && <ContrastFormControl size="small" data-joyride-id={HEADER_TOUR_ANCHORS.datasetSelector} sx={{ marginLeft: isSmall ? '0px' : '8px', marginRight: isSmall ? '0px' : '32px', verticalAlign: 'middle', minWidth: isSmall ? '80px' : '150px', flexShrink: 1, marginTop: '4px' }}>
                <InputLabel id="dataset-select-label" size="small"
                  disableAnimation>Dataset</InputLabel>
                <ContrastSelect
                  aria-label="Dataset select"
                  labelId="dataset-select-label"
                  id="dataset-select"
                  value={selectorValue}
                  label="Dataset"
                  onChange={(e) => handleSelectorChange(e.target.value)}
                  size="small"
                  MenuProps={{
                    container: mainElement
                  }}
                >
                  {selectorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.isAction ? <em>{option.label}</em> : option.label}
                    </MenuItem>
                  ))}
                  {/* Keyed on the datasets, not the whole option list: with
                      none choosable the value falls back to this sentinel, and
                      a page lending the selector its own entries (a draft, "New
                      graph…") must not leave that value without an option. */}
                  {datasetOptions.length === 0 && (
                    <MenuItem disabled value="Empty">
                      <em>No datasets</em>
                    </MenuItem>
                  )}
                </ContrastSelect>
              </ContrastFormControl>}
                </Box>
              )}
            {/* The rail's slot, and the row's only claimant on the slack.
                The row is the nearest box with a width of its own, so a
                `1 1 0` child of it is sized from what the row has left --
                which is what makes the width an answer rather than an echo.
                One level lower the same declaration reads the group's
                content instead, and the rail's own pills are in it. */}
            {!isMobile && <HeaderTaskbar tools={tools} pinned={pinnedTools} />}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flex: '0 0 auto',
                minWidth: 0,
                gap: isMobile ? 0 : '8px'
              }}
            >

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 0px'
                }}
              >
              {/* Fixed width, every one of these: the rail beside them is
                  what absorbs the row's slack, and a group that resized too
                  would leave nothing steady to aim at. Nothing needs a rule
                  between the panel launchers and the app's own controls now
                  that every one of them carries its name.

                  Mobile keeps the chat launcher and loses the rest: plugin
                  surfaces open as modals there, and a floating panel would
                  be a window a phone has no rail to park it back into, so
                  the launcher stands in for the rail rather than joining
                  it. */}
              {isMobile && <AskAiPill />}
                <HelpIcon />

              <DemoDataMenu
                open={demoOpen}
                anchorEl={demoAnchorEl}
                isLoading={demoIsLoading}
                isLoaded={demoLoaded}
                onLoad={loadDemoData}
                onClose={() => setDemoOpen(false)}
              />
              {!isMobile && (
                <>
                  {/* Deployment administration: what it changes, every user
                      sees. That is the whole reason it is its own control
                      rather than an item inside someone's account menu. */}
                  {userProfile?.userIsAdmin && (
                    <HeaderControl
                      icon={<AdminPanelSettingsIcon fontSize="inherit" />}
                      label="Admin"
                      isLabelHidden={displayMode === 'icons'}
                      isIconHidden={displayMode === 'titles'}
                      description="Plugins and deployment settings"
                      onClick={() => openSettings('plugins', 'admin')}
                    />
                  )}
                </>
              )}
              {isMobile && (
                <Tooltip title="Application Menu">
                  <IconButton
                    aria-label="Application Menu"
                    onClick={() => setDrawerOpen(true)}
                    sx={{
                      padding: '5px',
                      borderRadius: theme.border.radius,
                      color: theme.palette.menuPanel.default.text,
                      '&:hover': {
                        backgroundColor: theme.palette.menuPanel.hover.background
                      }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
              )}
              {!isMobile && (
                <>
                  <HeaderIdentityMenu
                    username={activeUser ?? null}
                    canSignOut={
                      sessionStorage.getItem('singleUserMode') !== 'true'}
                    onOpenSettings={() => openSettings(undefined, 'user')}
                    onOpenTools={tools.length > 0 ?
                      setToolsAnchor : undefined}
                    onLogout={handleLogout}
                  />
                  <ToolsPopover
                    tools={tools}
                    pinned={pinnedTools}
                    anchor={toolsAnchor}
                    onClose={() => setToolsAnchor(null)}
                  />
                </>
              )}
              </Box>
            </Box>
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <HelpIcon />
            </div>
          )}
        </div>

        {isAuthenticated() && isMobile && (
           <Box
             sx={{
               display: 'flex',
               justifyContent: 'flex-start',
               alignItems: 'center',
               padding: '4px 0px',
               width: '100%',
             }}
           >
              <LLMSelector />
              <ContrastFormControl size="small" sx={{ minWidth: 150, marginLeft: '8px' }}>
                <InputLabel id="mobile-dataset-select-label" size="small">Dataset</InputLabel>
                <ContrastSelect
                  aria-label="Dataset select"
                  labelId="mobile-dataset-select-label"
                  id="mobile-dataset-select"
                  value={selectorValue}
                  label="Dataset"
                  onChange={(e) => handleSelectorChange(e.target.value)}
                  size="small"
                >
                  {selectorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.isAction ? <em>{option.label}</em> : option.label}
                    </MenuItem>
                  ))}
                  {/* Keyed on the datasets, not the whole option list: with
                      none choosable the value falls back to this sentinel, and
                      a page lending the selector its own entries (a draft, "New
                      graph…") must not leave that value without an option. */}
                  {datasetOptions.length === 0 && (
                    <MenuItem disabled value="Empty">
                      <em>No datasets</em>
                    </MenuItem>
                  )}
                </ContrastSelect>
              </ContrastFormControl>
           </Box>
        )}

        {isAuthenticated() && !isMobile && (
          <div
            id="application-menu"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderTop: theme.border.thick,
              padding: '2px 0px',
              width: '100%',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                paddingRight: '4px',
                backgroundColor: theme.palette.menuPanel.default.background,
                opacity: (canScroll && isScrolled) ? 1 : 0,
                pointerEvents: (canScroll && isScrolled) ? 'auto' : 'none',
              }}
            >
              <IconButton
                size="small"
                aria-label="Scroll menu left"
                onClick={() => handleScroll('left')}
                sx={{
                  borderRadius: theme.border.radius,
                  color: theme.palette.menuPanel.default.text,
                  '&:hover': {
                    backgroundColor: theme.palette.menuPanel.hover.background
                  }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Box>
            <HeaderMenu
              ref={menuRef}
              onScroll={() => checkScroll()}
              role="menu"
              aria-label="Application Menu"
              style={{ width: '100%', position: 'relative', zIndex: 0 }}
            >
              {authenticatedMenuItems.map((item) => {
                return (
                  <Tooltip key={item.link} title={item.title}>
                    <HeaderMenuItem
                      isSelected={selectedMenuItem === item.title}
                      onClick={() => handleAuthenticatedMenuClick(item)}
                      onKeyDown={(event) => handleKeyDown(event, handleAuthenticatedMenuClick.bind(null, item))}
                      tabIndex={0}
                      role="menuitem"
                      aria-label={`${item.title}`}
                      data-joyride-id={`nav-${item.link.replace(/^\//, '')}`}
                    >
                      {displayMode !== 'titles' && item.icon}
                      {displayMode !== 'icons' && item.title}
                    </HeaderMenuItem>
                  </Tooltip>);
              })}
            </HeaderMenu>
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '4px',
                backgroundColor: theme.palette.menuPanel.default.background,
                opacity: (canScroll && !isScrolled) ? 1 : 0,
                pointerEvents: (canScroll && !isScrolled) ? 'auto' : 'none',
              }}
            >
              <IconButton
                size="small"
                aria-label="Scroll menu right"
                onClick={() => handleScroll('right')}
                sx={{
                  borderRadius: theme.border.radius,
                  color: theme.palette.menuPanel.default.text,
                  '&:hover': {
                    backgroundColor: theme.palette.menuPanel.hover.background
                  }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </div>
        )}
      </nav>
      {/* Mobile Drawer Menu */}
      {isAuthenticated() && isMobile && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: theme.palette.menuPanel.default.background,
              width: 180,
            }
          }}
        >
          <Box sx={{ width: 180, paddingTop: 2 }} role="presentation">
            {/* User Account Section */}
            <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountCircleIcon sx={{ color: theme.palette.menuPanel.default.text }} />
              <Typography sx={{ color: theme.palette.menuPanel.default.text, fontWeight: 'bold' }}>
                {activeUser}
              </Typography>
            </Box>
            <Divider />

            {/* Navigation Items */}
            <List>
              {authenticatedMenuItems.map((item) => (
                <ListItem key={item.link} disablePadding>
                  <ListItemButton
                    selected={selectedMenuItem === item.title}
                    onClick={() => handleAuthenticatedMenuClick(item)}
                    sx={{
                      color: theme.palette.menuPanel.default.text,
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.menuPanel.default.text,
                      },
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.menuPanel.selected.background,
                        color: theme.palette.menuPanel.selected.text,
                        '& .MuiListItemIcon-root': {
                          color: theme.palette.menuPanel.selected.text,
                        },
                      },
                      '&:hover, &.Mui-focusVisible, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
                        backgroundColor: theme.palette.menuPanel.hover.background,
                        color: theme.palette.menuPanel.hover.text,
                        '& .MuiListItemIcon-root': {
                          color: theme.palette.menuPanel.hover.text,
                        }
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.title} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ borderColor: theme.palette.border.default, my: 1, mx: 2 }} />

            {/* Settings & Logout */}
            <List>
              <SettingsDrawerSection
                isAdmin={userProfile?.userIsAdmin ?? false}
                onOpenSettings={handleSettings}
                onOpenServerSettings={handleDrawerServerSettings}
                onOpenAdminSettings={handleDrawerAdminSettings}
              />
              <DemoDrawerSection
                isLoading={demoIsLoading}
                isLoaded={demoLoaded}
                onLoad={loadDemoData}
              />
              <PluginsDrawerSection
                rows={pluginSurfaceRows}
                onSelect={handleDrawerSurfaceClick}
              />
              {sessionStorage.getItem('singleUserMode') !== 'true' && (
                <ListItem key="logout" disablePadding>
                  <ListItemButton
                    onClick={handleLogout}
                    sx={{
                      color: theme.palette.menuPanel.default.text,
                      '&:hover, &.Mui-focusVisible': {
                        backgroundColor: theme.palette.menuPanel.hover.background,
                        color: theme.palette.menuPanel.hover.text,
                        '& .MuiListItemIcon-root': {
                          color: theme.palette.menuPanel.hover.text,
                        }
                      },
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.menuPanel.default.text,
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Box>
        </Drawer>
      )}
      { isAuthenticated() && configData && configData.expiration_date !== '' &&
        (
          <Box sx={{ marginX: '-10px' }}>
            <ExpirationBanner configData={configData as { expiration_date: string }} />
          </Box>
        )
      }
      {openSurface && (
        <PluginSurfaceDispatcher
          plugin={openSurface.plugin}
          surface={openSurface.surface}
          onClose={() => setOpenSurface(null)}
        />
      )}
    </Box>
  );
};

export default Header;
